/**
 * Asset utility functions for handling images and videos with UploadCare integration
 * Supports both legacy path-based assets and UploadCare UUID-based assets
 */

/**
 * Build image asset URL for UC Asset Endpoint (GET /api/v2/public/asset/uc/<uuid>).
 * Uses recommended fast path: type=image + redirect=1 + preset + extras=webp_80
 * so the backend skips DB lookup and returns a cached 302 (browser caches 1h).
 * 
 * Supports both preset-based and dynamic sizing:
 * - Preset: Uses predefined size presets (e.g., 'medium', 'large', 'profile', 'instagram_square')
 * - Dynamic: Uses custom_ops with scale_crop for exact dimensions based on container size
 * 
 * The scale_crop operation with 'smart' alignment intelligently crops and scales images
 * to fit exact dimensions while preserving faces and important content.
 * 
 * @see https://uploadcare.com/docs/transformations/image/ for available operations
 * 
 * Available presets (52 total):
 * - Standard: thumbnail, small, medium, large, xlarge, wide, hero, full, 4k
 * - Profile: profile, profile_sm, profile_md, profile_lg, avatar, avatar_sm, avatar_lg
 * - Social: og_image, twitter_card, instagram_square, instagram_portrait, facebook_cover
 * - E-commerce: product, product_sm, product_lg, product_thumb, product_zoom
 * 
 * Available extras (129 options):
 * - Format: webp_80, webp_90, avif_90, jpeg_light, jpeg_high, png, auto_fmt
 * - Effects: enhance, sharpen, blur, gray, invert, vintage, sepia
 * - Shape: circle, round8-50, rot90/180/270, flip, mirror
 */
/** Default format extra for image optimization - WebP at 80% quality (~30-50% smaller than JPEG) */
const DEFAULT_IMAGE_EXTRAS = 'webp_80';

/**
 * Get the optimal image preset for hero/slideshow images based on screen size.
 * This avoids downloading 3000×1687 images on mobile when 800×450 would suffice.
 * 
 * Preset sizes:
 * - mobile (< 640px): 'wide' = 1280×720 (~50-100KB)
 * - tablet (640-1024px): 'hero' = 1920×1080 (~100-200KB)
 * - desktop (> 1024px): 'slideshow' = 3000×1687 (~200-400KB)
 * 
 * @param isMobile - Whether the device is mobile (optional, will detect if not provided)
 * @returns Optimal preset string
 */
export const getHeroPreset = (isMobile?: boolean): AssetProfile => {
  if (typeof window === 'undefined') {
    // SSR: default to medium-sized image
    return 'hero';
  }
  
  const width = window.innerWidth;
  const dpr = window.devicePixelRatio || 1;
  
  // Calculate effective width considering DPR (but cap at 2x to avoid excessive sizes)
  const effectiveWidth = width * Math.min(dpr, 2);
  
  // For mobile, use 'wide' preset (1280×720) - good for up to ~640px screens at 2x DPR
  if (isMobile !== undefined) {
    return isMobile ? 'wide' : 'slideshow';
  }
  
  // Auto-detect based on screen width
  if (effectiveWidth <= 1280) {
    return 'wide';       // 1280×720 - mobile and small tablets
  } else if (effectiveWidth <= 1920) {
    return 'hero';       // 1920×1080 - tablets and small desktops
  } else {
    return 'slideshow';  // 3000×1687 - large desktops and high-DPR displays
  }
};

/**
 * Get the optimal card image preset based on screen size.
 * Avoids downloading 320×240 images when displaying 56×56 thumbnails.
 * 
 * @param displaySize - The CSS pixel size of the image container ('xs' | 'sm' | 'md' | 'lg')
 * @returns Optimal preset string
 */
export const getCardPreset = (displaySize: 'xs' | 'sm' | 'md' | 'lg' = 'sm'): AssetProfile => {
  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
  const effectiveDpr = Math.min(dpr, 2); // Cap at 2x
  
  // Map display sizes to appropriate presets based on actual displayed size
  switch (displaySize) {
    case 'xs':  // ~56px thumbnails
      return effectiveDpr > 1 ? 'thumbnail' : 'thumbnail';     // 150×150
    case 'sm':  // ~112px card thumbnails
      return effectiveDpr > 1 ? 'small' : 'thumbnail';          // 320×240 or 150×150
    case 'md':  // ~300px cards
      return effectiveDpr > 1 ? 'medium' : 'small';             // 640×480 or 320×240
    case 'lg':  // ~500px+ cards
      return effectiveDpr > 1 ? 'large' : 'medium';             // 1024×768 or 640×480
    default:
      return 'small';
  }
};

function buildImageAssetURL(uuid: string, options: ImageAssetOptions): string {
  const params = new URLSearchParams()
  params.set('type', 'image') // Skip asset type DB lookup (fast path)
  params.set('redirect', options.redirect !== false ? '1' : '0')
  
  // Use width/height for dynamic sizing if provided, otherwise fall back to preset
  if (options.width && options.height) {
    // Dynamic dimensions: use custom_ops with scale_crop for smart cropping
    // Format matches backend's _size_to_op: scale_crop/WIDTHxHEIGHT/smart
    // 'smart' alignment ensures faces and important content are preserved
    const width = Math.round(options.width)
    const height = Math.round(options.height)
    const resizeOp = `scale_crop/${width}x${height}/smart`
    params.set('custom_ops', resizeOp)
  } else if (options.preset) {
    params.set('preset', options.preset)
  }
  
  // Always use format optimization - WebP/AVIF can reduce file size by 30-80%
  params.set('extras', options.extras ?? DEFAULT_IMAGE_EXTRAS)
  
  return `/public/asset/uc/${uuid}?${params.toString()}`
}

/**
 * Build video asset URL for UC Asset Endpoint.
 * Uses type=video + redirect=1 so the backend skips asset type DB lookup.
 * 
 * Supports video optimization, format conversion, and optional snippet/clip creation.
 * 
 * @param uuid - Uploadcare UUID
 * @param options - Video options including presets, format, and optional cut parameters
 * @returns Optimized video URL
 * 
 * @example
 * // Optimized 720p video
 * buildVideoAssetURL(uuid, { type: 'video', videoPreset: '720p', formatPreset: 'mp4_web' })
 * 
 * @example
 * // 30-second snippet from start
 * buildVideoAssetURL(uuid, { type: 'video', videoPreset: '480p', cutStart: '0:00', cutLength: '30' })
 */
/**
 * Build video asset URL for UploadCare CDN.
 * Params: format_preset, video_preset, video_format, video_quality, thumbnail, redirect, filename.
 */
function buildVideoAssetURL(uuid: string, options: VideoAssetOptions): string {
  const params = new URLSearchParams()
  params.set('type', 'video')
  params.set('redirect', options.redirect !== false ? '1' : '0')

  if (options.formatPreset) params.set('format_preset', options.formatPreset)
  if (options.videoPreset) params.set('video_preset', options.videoPreset)
  if (options.videoFormat) params.set('video_format', options.videoFormat)
  if (options.videoQuality) params.set('video_quality', options.videoQuality)
  if (options.thumbnail) params.set('thumbnail', options.thumbnail)
  if (options.filename) params.set('filename', options.filename)

  // Cut/snippet via custom_ops if backend supports it
  if (options.cutStart) {
    const cutLength = options.cutLength || 'end'
    const cutOp = `cut/${options.cutStart}/${cutLength}`
    params.set('custom_ops', cutOp)
  }

  return `/public/asset/uc/${uuid}?${params.toString()}`
}

/**
 * Get asset URL for images or videos with UploadCare integration
 * 
 * @param asset - Asset object with path or UUID information
 * @param optionsOrProfile - Asset profile (legacy) or options object for advanced control
 * @returns Asset URL or undefined if asset is invalid
 * 
 * @example
 * // Image (legacy, backward compatible)
 * getAsset(asset) // defaults to 'medium'
 * getAsset(asset, 'small')
 * getAsset(asset, 'large')
 * 
 * @example
 * // Image (new syntax)
 * getAsset(asset, { type: 'image', preset: 'medium', extras: 'webp_80' })
 * 
 * @example
 * // Video
 * getAsset(asset, { type: 'video', videoPreset: '720p', formatPreset: 'mp4_web' })
 * getAsset(asset, { type: 'video', thumbnail: 'poster' })
 */
/**
 * Resolve the best image asset for a promotion slide.
 * Prioritizes promotion_assets over entity.assets.
 * Supports both uuid_uc (UploadCare) and path/path_root (legacy) assets.
 */
export const getPromotionSlideAsset = (slide: {
  promotion_assets?: Array<{ uuid_uc?: string | null; path?: string | null; path_root?: string | null }> | null;
  entity?: { assets?: Array<{ uuid_uc?: string | null; path?: string; path_root?: string; default?: number }> };
} | null): Asset | null => {
  if (!slide) return null;
  // 1. Prioritize promotion_assets
  if (slide.promotion_assets?.length) {
    return slide.promotion_assets[0] as Asset;
  }
  // 2. Fall back to entity.assets
  const entityAssets = slide.entity?.assets;
  if (entityAssets?.length) {
    const defaultAsset = entityAssets.find((a) => a.default);
    return (defaultAsset ?? entityAssets[0]) as Asset;
  }
  return null;
};

export const getAsset = (
  asset?: Asset | null,
  optionsOrProfile?: AssetProfile | AssetOptions
): string | undefined => {
  if (!asset) return getAssetPlaceholder()

  const CDNBaseURL = import.meta.env.VITE_PUBLIC_CDN_REQUEST_SERVER || 'https://cdn.feelyfeely.cloud'

  // Backward compatibility: handle string profile or undefined
  const isLegacyCall = typeof optionsOrProfile === 'string' || !optionsOrProfile

  // Endpoint defaults to type=image; for video UUIDs we must pass type=video or we get wrong URL / broken playback
  const isVideoAsset = typeof (asset as any).mime_type === 'string' && (asset as any).mime_type.toLowerCase().startsWith('video/') || (asset as any).type === 'video'

  // Determine options: auto-detect video so legacy getAsset(video) never requests image mode for a video UUID
  const options: AssetOptions = isLegacyCall
    ? (isVideoAsset
        ? { type: 'video', videoPreset: '720p', formatPreset: 'mp4_web', redirect: true }
        : { type: 'image', preset: (optionsOrProfile as AssetProfile) || 'medium', redirect: true })
    : optionsOrProfile as AssetOptions

  // Proxy only for a real Uploadcare id. Listing assets put their own id in `uuid`
  // and the file in `public_url`; treating that id as Uploadcare 404s on ucarecdn.
  // Payloads with no URL still pass the UC id in `uuid` (profile photos).
  const ucId = (v: unknown) => {
    const s = v == null ? '' : String(v)
    const low = s.toLowerCase()
    return s && low !== 'none' && low !== 'null' ? s : undefined
  }
  const http = (v?: string | null) => !!(v && (v.startsWith('https://') || v.startsWith('http://')))
  let uuidUc = ucId(asset.uuid_uc)
  if (!uuidUc && !http(asset.public_url_uc) && !http(asset.public_url) && !(asset.path_root && asset.path)) {
    uuidUc = ucId((asset as any).uuid)
  }
  if (uuidUc) {
    if (options.type === 'video') {
      return buildVideoAssetURL(uuidUc, options as VideoAssetOptions)
    }
    return buildImageAssetURL(uuidUc, options as ImageAssetOptions)
  }
  if (asset.public_url_uc && (asset.public_url_uc.startsWith('https://') || asset.public_url_uc.startsWith('http://'))) return asset.public_url_uc
  if (asset.public_url && (asset.public_url.startsWith('https://') || asset.public_url.startsWith('http://'))) return asset.public_url
  if (asset.path_root && asset.path) {
    return options.type === 'video'
      ? `${CDNBaseURL}/public/video/${asset.path_root}/${asset.path}`
      : `${CDNBaseURL}/public/image/${asset.path_root}/${asset.path}`
  }

  return undefined
}

/**
 * Direct CDN URL from `path_root` + `path` (same rule as the path branch in {@link getAsset}).
 * Use when the API includes both `uuid` and paths: {@link getAsset} prefers the UC proxy
 * (`/public/asset/uc/...`), which can fail while this CDN URL still works.
 */
export function getCdnImageUrlFromAsset(
  asset?: { path_root?: string | null; path?: string | null } | null,
): string | undefined {
  if (!asset?.path_root || !asset?.path) return undefined
  const CDNBaseURL = import.meta.env.VITE_PUBLIC_CDN_REQUEST_SERVER || 'https://cdn.feelyfeely.cloud'
  return `${CDNBaseURL}/public/image/${asset.path_root}/${asset.path}`
}

/**
 * Get video source URL with preset quality and format
 * 
 * @param asset - Asset object
 * @param preset - Video resolution preset (default: '720p')
 * @param format - Video format preset (default: 'mp4_web')
 * @returns Video source URL or undefined
 * 
 * @example
 * getVideoAsset(asset, '1080p', 'mp4_high')
 * getVideoAsset(asset, '720p', 'webm_web')
 */
export const getVideoAsset = (
  asset?: Asset | null,
  preset: VideoPreset = '720p',
  format: VideoFormatPreset = 'mp4_web'
): string | undefined => {
  return getAsset(asset, {
    type: 'video',
    videoPreset: preset,
    formatPreset: format,
    redirect: true
  })
}

/**
 * Get video thumbnail/poster image
 * 
 * @param asset - Asset object
 * @param thumbnailType - Thumbnail type (default: 'poster')
 * @returns Thumbnail URL or undefined
 * 
 * @example
 * getVideoThumbnail(asset, 'poster')
 * getVideoThumbnail(asset, 'preview')
 * getVideoThumbnail(asset, 'card')
 */
export const getVideoThumbnail = (
  asset?: Asset | null,
  thumbnailType: VideoThumbnailType = 'poster'
): string | undefined => {
  return getAsset(asset, {
    type: 'video',
    thumbnail: thumbnailType,
    redirect: true
  })
}

/**
 * Get video with custom format and quality
 * 
 * @param asset - Asset object
 * @param format - Video format (mp4, webm, ogg)
 * @param quality - Video quality level
 * @returns Video source URL or undefined
 * 
 * @example
 * getVideoWithFormat(asset, 'webm', 'best')
 * getVideoWithFormat(asset, 'mp4', 'normal')
 */
export const getVideoWithFormat = (
  asset?: Asset | null,
  format: VideoFormat = 'mp4',
  quality: VideoQuality = 'normal'
): string | undefined => {
  return getAsset(asset, {
    type: 'video',
    videoFormat: format,
    videoQuality: quality,
    redirect: true
  })
}

// Placeholder asset UUID - ensure this asset exists in the database
// To verify: Check that an asset with uuid_uc='a0dfd844-e2e7-495a-9871-b3661f485e81' exists in the asset table
// with active=1 and deleted=0
const PLACEHOLDER_ASSET_UUID = 'a0dfd844-e2e7-495a-9871-b3661f485e81';

/** True when src is the shared missing-image placeholder from {@link getAssetPlaceholder} (not every UC proxy URL). */
export const isPlaceholderAssetUrl = (src: string | null | undefined): boolean => {
  if (!src || typeof src !== 'string') return false;
  return src.includes(PLACEHOLDER_ASSET_UUID);
};

/** Base URL for direct UploadCare CDN access (bypasses proxy) */
const UC_CDN_BASE = import.meta.env.VITE_PUBLIC_UC_REQUEST_SERVER || 'https://ucarecdn.com';

/** UUID regex for extracting from proxy URLs */
const PROXY_ASSET_UUID_RE = /^\/public\/asset\/uc\/([a-f0-9-]{36})/i;

/**
 * Extract direct UploadCare CDN fallback URL from a proxy URL.
 * Use when the proxy (/public/asset/uc/...) fails to load (e.g. backend down, redirect issues).
 * @param proxyUrl - URL like /public/asset/uc/<uuid>?type=image&...
 * @param size - Preset for fallback dimensions (default 'medium')
 */
export const getDirectFallbackFromProxyUrl = (
  proxyUrl: string,
  size: 'small' | 'medium' | 'large' = 'medium'
): string | null => {
  if (!proxyUrl || typeof proxyUrl !== 'string') return null;
  const match = proxyUrl.match(PROXY_ASSET_UUID_RE);
  if (!match) return null;
  const ucSize: 'small' | 'medium' | 'slideshow' =
    size === 'large' ? 'slideshow' : size;
  return getDirectUploadCareImageUrl(match[1], ucSize);
};

/** Model id from Matterport showcase URL (`?m=`). */
export function extractMatterportModelId(tourUrl: string): string | null {
  if (!tourUrl || typeof tourUrl !== 'string') return null;
  const m = tourUrl.match(/[?&]m=([a-zA-Z0-9_-]+)/);
  return m?.[1] ?? null;
}

/**
 * Public thumbnail image for a Matterport tour (same id as `m=` in the embed URL).
 * @see https://stackoverflow.com/questions/42335148/get-matterport-thumbnail
 */
export function getMatterportThumbnailUrl(tourUrl: string): string | undefined {
  const id = extractMatterportModelId(tourUrl);
  if (!id) return undefined;
  return `https://my.matterport.com/api/v1/player/models/${id}/thumb`;
}

/**
 * Ordered image URL candidates for 3D tour strip thumbnails. UC image proxy often fails for
 * `3dtour` / model assets whose UUID is not a raster; Matterport tours should use the API thumb first.
 */
export function getVirtualTourThumbnailCandidates(
  asset: Parameters<typeof getAsset>[0] | null | undefined,
  tourUrl: string,
  explicitThumb?: string | null,
  fallbackImageUrl?: string | null,
): string[] {
  const out: string[] = [];
  const add = (u?: string | null) => {
    if (u && typeof u === 'string') {
      const t = u.trim();
      if (t && !out.includes(t)) out.push(t);
    }
  };

  add(explicitThumb ?? undefined);
  add(getMatterportThumbnailUrl(tourUrl));

  if (asset) {
    const proxy = getAsset(asset, {
      type: 'image',
      preset: 'thumbnail',
      redirect: true,
    });
    add(proxy);
    add(getCdnImageUrlFromAsset(asset));
    if (asset.public_url_uc && /^https?:\/\//i.test(asset.public_url_uc)) {
      add(asset.public_url_uc);
    }
    if (asset.public_url && /^https?:\/\//i.test(asset.public_url)) {
      add(asset.public_url);
    }
    if (proxy) {
      add(getDirectFallbackFromProxyUrl(proxy, 'small'));
    }
  }

  add(fallbackImageUrl ?? undefined);
  return out;
}

/**
 * Build direct UploadCare CDN URL - bypasses backend proxy.
 * Use as fallback when /public/asset/uc/ proxy fails to load.
 * Includes format/auto (WebP/AVIF) and quality/lighter for optimized delivery.
 * @param uuid - UploadCare UUID
 * @param size - Optional preset: 'small' (112x112), 'medium' (400x300), 'slideshow' (1200x675)
 */
export const getDirectUploadCareImageUrl = (uuid: string, size: 'small' | 'medium' | 'slideshow' = 'small'): string => {
  const dims: Record<string, string> = {
    small: '112x112',
    medium: '400x300',
    slideshow: '1200x675',
  };
  const d = dims[size] || dims.small;
  // format/auto: AVIF for supported browsers, WebP for others - significantly reduces file size
  // quality/lighter: for slideshow/medium reduces ~20% more with minimal quality loss
  const qualitySuffix = (size === 'slideshow' || size === 'medium') ? '-/quality/lighter/' : '';
  return `${UC_CDN_BASE}/${uuid}/-/scale_crop/${d}/center/-/format/auto/${qualitySuffix}`;
};

// Fallback data URI for placeholder (1x1 transparent PNG) if asset doesn't exist
const FALLBACK_PLACEHOLDER_DATA_URI = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzk3YTNiNCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlPC90ZXh0Pjwvc3ZnPg==';

/**
 * Get placeholder asset URL
 * @param profile - Asset profile/preset (e.g., 'small', 'medium', 'large', 'slideshow')
 * @returns URL to placeholder asset or fallback data URI
 * 
 * Note: Ensure the placeholder asset exists in the database:
 * - uuid_uc = 'a0dfd844-e2e7-495a-9871-b3661f485e81'
 * - active = 1
 * - deleted = 0
 * - Has valid path_root and path values
 */
export const getAssetPlaceholder = ( profile?: AssetProfile ): string => {
  const src = `/public/asset/uc/${PLACEHOLDER_ASSET_UUID}?type=image&redirect=1&extras=${DEFAULT_IMAGE_EXTRAS}${profile ? `&preset=${profile}` : ''}`;
  return src;
}

/**
 * Fallback image URL when the proxy placeholder fails (e.g. backend down).
 * Uses direct UploadCare CDN so the browser can still show a placeholder.
 * Use as fallbackSrc in ImageWithPlaceholder when the primary src is getAssetPlaceholder().
 */
export const getAssetPlaceholderFallback = (size: 'small' | 'medium' | 'slideshow' = 'small'): string => {
  return getDirectUploadCareImageUrl(PLACEHOLDER_ASSET_UUID, size);
};

/**
 * Verify placeholder asset exists by attempting to load it
 * This is a client-side check - for server-side verification, query the database directly
 * @param profile - Optional asset profile to test (default: 'small')
 * @returns Promise that resolves to an object with verification details
 */
export const verifyPlaceholderAsset = async (profile: AssetProfile = 'small'): Promise<{
  exists: boolean;
  status: number;
  statusText: string;
  url: string;
  error?: string;
}> => {
  const placeholderUrl = getAssetPlaceholder(profile);
  
  try {
    const response = await fetch(placeholderUrl, { 
      method: 'HEAD', 
      cache: 'no-cache',
      redirect: 'follow'
    });
    
    const exists = response.ok || response.status === 302;
    
    return {
      exists,
      status: response.status,
      statusText: response.statusText,
      url: placeholderUrl
    };
  } catch (error: any) {
    return {
      exists: false,
      status: 0,
      statusText: 'Network Error',
      url: placeholderUrl,
      error: error?.message || String(error)
    };
  }
}

/**
 * Comprehensive placeholder asset test - tests multiple profiles
 * Can be called from browser console: window.testPlaceholderAsset()
 * @returns Promise with test results for all asset profiles
 */
export const testPlaceholderAsset = async (): Promise<{
  uuid: string;
  profiles: Record<string, { exists: boolean; status: number; url: string; error?: string }>;
  summary: { total: number; passed: number; failed: number };
}> => {
  const profiles: AssetProfile[] = ['small', 'medium', 'large', 'slideshow'];
  const results: Record<string, any> = {};
  
  console.log('🧪 Testing placeholder asset:', PLACEHOLDER_ASSET_UUID);
  console.log('Testing profiles:', profiles);
  
  for (const profile of profiles) {
    const result = await verifyPlaceholderAsset(profile);
    results[profile] = {
      exists: result.exists,
      status: result.status,
      url: result.url,
      error: result.error
    };
    
    const icon = result.exists ? '✅' : '❌';
    console.log(`${icon} ${profile}: ${result.status} ${result.statusText}`, result.url);
    
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  const passed = Object.values(results).filter((r: any) => r.exists).length;
  const failed = Object.values(results).filter((r: any) => r.exists === false).length;
  
  const summary = {
    total: profiles.length,
    passed,
    failed
  };
  
  console.log('\n📊 Summary:', summary);
  if (failed > 0) {
    console.warn('⚠️ Some placeholder asset profiles failed. Check the results above.');
  } else {
    console.log('✅ All placeholder asset profiles are working correctly!');
  }
  
  return {
    uuid: PLACEHOLDER_ASSET_UUID,
    profiles: results,
    summary
  };
}

// Expose to window for browser console access (development only; no console spam on load)
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  (window as any).testPlaceholderAsset = testPlaceholderAsset;
  (window as any).verifyPlaceholderAsset = verifyPlaceholderAsset;
}

/**
 * Get placeholder with fallback - returns data URI if asset doesn't exist
 * @param profile - Asset profile/preset
 * @param useFallback - Whether to use data URI fallback if asset fails to load
 * @returns URL to placeholder asset or fallback data URI
 */
export const getAssetPlaceholderWithFallback = ( profile?: AssetProfile, useFallback: boolean = true ): string => {
  if (!useFallback) {
    return getAssetPlaceholder(profile);
  }
  return getAssetPlaceholder(profile);
}

/**
 * Calculate optimal image dimensions based on container size and device pixel ratio
 * @param containerWidth - Container width in CSS pixels
 * @param containerHeight - Container height in CSS pixels
 * @param devicePixelRatio - Device pixel ratio (default: window.devicePixelRatio or 1)
 * @param aspectRatio - Optional aspect ratio to maintain (width/height)
 * @returns Object with optimal width and height in physical pixels
 */
export const calculateOptimalImageSize = (
  containerWidth: number,
  containerHeight: number,
  devicePixelRatio: number = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
  aspectRatio?: number
): { width: number; height: number } => {
  // Calculate physical pixel dimensions (accounting for DPR)
  let width = Math.ceil(containerWidth * devicePixelRatio);
  let height = Math.ceil(containerHeight * devicePixelRatio);
  
  // Round to nearest 8px for better CDN caching (common optimization)
  width = Math.ceil(width / 8) * 8;
  height = Math.ceil(height / 8) * 8;
  
  // Maintain aspect ratio if provided
  if (aspectRatio && aspectRatio > 0) {
    const calculatedHeight = Math.ceil(width / aspectRatio);
    if (Math.abs(calculatedHeight - height) / height > 0.1) {
      // Significant difference, adjust to maintain aspect ratio
      height = Math.ceil(calculatedHeight / 8) * 8;
    }
  }
  
  // Ensure minimum size
  width = Math.max(width, 32);
  height = Math.max(height, 32);
  
  return { width, height };
}

/**
 * Get optimal extras string for image format based on browser support
 * Prioritizes modern formats (AVIF > WebP > JPEG) for better compression
 * @returns Extras string with format optimization
 */
export const getOptimalImageExtras = (): string => {
  if (typeof window === 'undefined') {
    // Server-side: default to WebP
    return 'webp_80';
  }
  
  // Check for AVIF support (best compression)
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    // AVIF is supported in modern browsers
    const avifSupported = canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;
    if (avifSupported) {
      return 'avif_90';
    }
  }
  
  // Check for WebP support (good compression, wide support)
  const webpSupported = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  if (webpSupported) {
    return 'webp_80';
  }
  
  // Fallback to no format optimization (JPEG)
  return '';
}

/**
 * Get responsive asset URL optimized for container size and screen resolution
 * @param containerWidth - Container width in CSS pixels
 * @param containerHeight - Container height in CSS pixels
 * @param asset - Asset object (optional)
 * @param options - Additional options (extras, aspectRatio, etc.)
 * @returns Optimized asset URL
 * 
 * @example
 * // For a 56x56px card on a 2x display
 * getResponsiveAsset(56, 56, asset)
 * // Returns URL with width=112&height=112 (56 * 2 DPR)
 * 
 * @example
 * // With aspect ratio constraint
 * getResponsiveAsset(300, 200, asset, { aspectRatio: 16/9 })
 */
export const getResponsiveAsset = (
  containerWidth: number,
  containerHeight: number,
  asset?: Asset | null,
  options?: {
    devicePixelRatio?: number;
    aspectRatio?: number;
    extras?: string;
    useOptimalFormat?: boolean;
  }
): string | undefined => {
  if (!asset) return getAssetPlaceholder();
  
  // Calculate optimal dimensions
  const { width, height } = calculateOptimalImageSize(
    containerWidth,
    containerHeight,
    options?.devicePixelRatio,
    options?.aspectRatio
  );
  
  // Determine extras (format optimization)
  const extras = options?.useOptimalFormat !== false 
    ? (options?.extras || getOptimalImageExtras())
    : options?.extras;
  
  // Build options with dynamic dimensions
  const assetOptions: ImageAssetOptions = {
    type: 'image',
    width,
    height,
    extras: extras || undefined,
    redirect: true
  };
  
  return getAsset(asset, assetOptions);
}
